import React, { useMemo } from 'react';
import { Col, Row, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { selectors } from 'state';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

const MRRPlayerGraph: React.FC = () => {
  const session = useSelector(selectors.activeSession);
  const player = useSelector(selectors.player);

  const { starting_cash_amount } = session;

  // Categories like "1-4", "5-8", ...
  const categories = useMemo(() => {
    const hist = player.mrr_history ?? [];
    // Preserve existing order (Highcharts version didn’t sort the player series)
    return hist.map(
      (hs: any) => `${hs.block_index * 4 - 3}-${hs.block_index * 4}`
    );
  }, [player.mrr_history]);

  const playerData = useMemo(() => {
    const hist = player.mrr_history ?? [];
    return hist.map((hs: any) => {
      const frac = hs.four_period_average_cash / starting_cash_amount;
      return Number.isFinite(frac) ? frac : null;
    });
  }, [player.mrr_history, starting_cash_amount]);

  const benchmarkData = useMemo(() => {
    const hist = (player.mrr_history ?? [])
      .filter((hs: any) => hs.four_period_average_mrr != null)
      .sort((a: any, b: any) => a.block_index - b.block_index);

    // Need to align to categories (same x-axis categories as player)
    const mapByLabel = new Map<string, number>();
    for (const hs of hist) {
      const label = `${hs.block_index * 4 - 3}-${hs.block_index * 4}`;
      mapByLabel.set(label, hs.four_period_average_mrr as number);
    }

    return categories.map(label => {
      const v = mapByLabel.get(label);
      return typeof v === 'number' && Number.isFinite(v) ? v : null;
    });
  }, [player.mrr_history, categories]);

  const series = useMemo(() => {
    return [
      {
        name: player.name,
        type: 'line' as const,
        data: playerData,
      },
      {
        name: 'Required MRR',
        type: 'line' as const,
        data: benchmarkData,
      },
    ];
  }, [player.name, playerData, benchmarkData]);

  const options = useMemo((): ApexOptions => {
    return {
      chart: {
        type: 'line',
        height: '20%',
        toolbar: { show: false }, // keep consistent with your other charts; remove if you want the toolbar
        zoom: { enabled: false },
      },
      xaxis: {
        type: 'category',
        categories,
        title: { text: 'Periods' },
        labels: {
          // matches Highcharts category labels exactly (e.g. "1-4")
          formatter: (val: string) => val,
        },
      },
      yaxis: {
        labels: {
          formatter: (v: number) =>
            Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : String(v),
        },
      },
      legend: {
        show: true,
        position: 'right',
        horizontalAlign: 'center', // closest to vertical middle feel
      },
      stroke: {
        curve: 'straight',
        width: [3, 3],
        dashArray: [0, 6], // "Required MRR" dashed like ShortDash
      },
      markers: {
        size: [3, 0], // benchmark markers disabled like Highcharts
      },
      tooltip: {
        shared: false,
        intersect: true,
        y: {
          formatter: (y?: number) => {
            const v = Number(y);
            return Number.isFinite(v) ? `${(v * 100).toFixed(1)}%` : '-';
          },
          title: {
            formatter: (seriesName: string) => `${seriesName}: `,
          },
        },
      },
      grid: {
        padding: { left: 8, right: 8 },
      },
    };
  }, [categories]);

  return (
    <Row style={{ flexFlow: 'row nowrap' }} gutter={10}>
      <Col flex="1">
        <Typography.Title level={4}>Minimum Reserve Required</Typography.Title>
        <Chart
          options={options}
          series={series as any}
          type="line"
          height="20%"
        />
      </Col>
    </Row>
  );
};

export default MRRPlayerGraph;
